import { Question, WebsiteEvent } from "../../../resources/modelTypes";
import { TIMEZONE_OPTIONS } from "./constants";

/**
 * Get GMT offset string for a timezone (e.g., "GMT-05:00")
 */
export const getGMTOffset = (timezone: string): string | null => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });

    // If the browser doesn't recognize the timezone it silently falls back to
    // the local timezone, producing a bogus offset. Detect this specifically
    // by checking if the resolved timezone matches the local timezone and that
    // the given one matches the local timezone from the browser.
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const resolvedTimezone = formatter.resolvedOptions().timeZone;

    if (resolvedTimezone === localTimezone && timezone !== localTimezone) {
      return null;
    }

    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");

    if (offsetPart) {
      // Convert "GMT-5" to "GMT-05:00" format
      const offset = offsetPart.value;

      if (offset === "GMT") {
        return "(GMT+00:00)";
      }

      const match = offset.match(/GMT([+-])(\d+)(?::(\d+))?/);

      if (match) {
        const sign = match[1];
        const hours = match[2].padStart(2, "0");
        const minutes = match[3] || "00";

        return `(GMT${sign}${hours}:${minutes})`;
      }

      return `(${offset})`;
    }

    return "(GMT)";
  } catch {
    return null;
  }
};

/**
 * Get user's current timezone from the browser.
 * If the browser timezone is in TIMEZONE_OPTIONS, returns it directly.
 * If not, finds the first TIMEZONE_OPTIONS entry with the same UTC offset.
 * Falls back to America/New_York if no match is found.
 */
export const getUserTimezone = (): string => {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Check whether there is a direct timezone match before checking offset
    // against others in our default list of timezones
    if (TIMEZONE_OPTIONS.some((tz) => tz.value === browserTimezone)) {
      return browserTimezone;
    }

    // No direct match found, check browser offset and match against default list
    // of timezones.
    const browserOffset = getGMTOffset(browserTimezone);

    const match = TIMEZONE_OPTIONS.find(
      (tz) => getGMTOffset(tz.value) === browserOffset,
    );

    if (match) {
      return match.value;
    }

    return "America/New_York";
  } catch {
    return "America/New_York";
  }
};

export type SelectTimezoneOption = {
  text: string;
  value: string;
};

/**
 * Get formatted timezone options with GMT offset for Select component
 */
export const getTimezoneOptions = (): SelectTimezoneOption[] => {
  return TIMEZONE_OPTIONS.map((tz) => ({
    text: `${getGMTOffset(tz.value)} ${tz.label}`,
    value: tz.value,
  })).sort((tzA, tzB) => {
    // Sort by GMT offset (extract hours for comparison)
    const getOffsetMinutes = (text: string): number => {
      const match = text.match(/GMT([+-])(\d+):(\d+)/);

      if (match) {
        const sign = match[1] === "-" ? -1 : 1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);

        return sign * (hours * 60 + minutes);
      }

      return 0;
    };

    return getOffsetMinutes(tzA.text) - getOffsetMinutes(tzB.text);
  });
};

type RequiredAddressField =
  | "address1"
  | "city"
  | "state"
  | "zipCode"
  | "country";

export const requiredAddressFields: RequiredAddressField[] = [
  "address1",
  "city",
  "state",
  "zipCode",
  "country",
];

/**
 * Start time, end date, and end time are all optional — a date range is
 * valid whenever there's no end date, or the end is not before the start.
 */
export const areDatesValid = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): boolean => {
  if (!endDate) {
    return true;
  }
  const startDateTime = new Date(`${startDate} ${startTime || "12:00 PM"}`);
  const endDateTime = new Date(`${endDate} ${endTime || "12:00 PM"}`);

  return startDateTime <= endDateTime;
};

/** Max years ahead an Online Invitation event date may be set. */
export const MAX_OI_EVENT_YEARS_AHEAD = 5;

export const OI_EVENT_DATE_TOO_FAR_ERROR =
  "Event date can’t be more than 5 years from today";

/**
 * Parse MM/dd/yyyy or YYYY-MM-DD as a local calendar date.
 * Prefer this over `new Date(isoString)` so YYYY-MM-DD is not treated as UTC.
 */
export const parseLocalEventDate = (date: string): Date | null => {
  if (!date) {
    return null;
  }

  if (date.includes("/")) {
    const parsedDate = new Date(date);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const [year, month, day] = date.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

/**
 * Whether an OI event date is more than {@link MAX_OI_EVENT_YEARS_AHEAD}
 * years after now. Accepts a local Date, or MM/dd/yyyy / YYYY-MM-DD strings.
 */
export const isOiEventDateTooFarAhead = (date: string | Date): boolean => {
  const parsedDate =
    typeof date === "string" ? parseLocalEventDate(date) : date;

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + MAX_OI_EVENT_YEARS_AHEAD);

  return parsedDate > maxDate;
};

export type EventValidationData = Pick<
  WebsiteEvent,
  | "title"
  | "hasPhysicalAddress"
  | RequiredAddressField
  | "date"
  | "time"
  | "endDate"
  | "endTime"
  | "questions"
>;

export type EventValidationResult = {
  erroredQuestionIndexes: Set<number>;
  invalidFields: string[];
};

/**
 * Pure validation shared by the desktop Save button (EventForm's
 * validateAndSaveForm) and the mobile autosave-on-close path
 * so closing the swipe panel can detect invalid data without dispatching a network
 * request.
 */
export const getEventValidationErrors = (
  eventData: EventValidationData,
  {
    isOnlineInvitation,
    isWedding,
  }: { isOnlineInvitation?: boolean; isWedding: boolean },
): EventValidationResult => {
  const invalidFields: string[] = [];

  if (isWedding && !eventData.title) {
    invalidFields.push("title");
  }

  if (eventData.hasPhysicalAddress) {
    for (const requiredField of requiredAddressFields) {
      if (!eventData[requiredField]) {
        invalidFields.push(requiredField);
      }
    }
  }

  if (
    !areDatesValid(
      eventData.date,
      eventData.time,
      eventData.endDate,
      eventData.endTime,
    )
  ) {
    invalidFields.push("eventDateRange");
  }

  if (isOnlineInvitation && isOiEventDateTooFarAhead(eventData.date)) {
    invalidFields.push("eventDateTooFar");
  }

  const erroredQuestionIndexes = new Set<number>();

  eventData.questions.forEach((question: Question, index: number) => {
    const isMissingText = !question.text;
    const hasEmptyChoice =
      question.questionType === "multiple_choice" &&
      (question.choices ?? []).some((choice) => !choice);

    if (isMissingText || hasEmptyChoice) {
      erroredQuestionIndexes.add(index);
    }
  });

  return { erroredQuestionIndexes, invalidFields };
};

/**
 * Get display text for a timezone value
 */
export const getTimezoneDisplayText = (timezone: string | null): string => {
  if (!timezone) {
    return "";
  }

  const option = TIMEZONE_OPTIONS.find((tz) => tz.value === timezone);

  if (option) {
    return `${getGMTOffset(timezone)} ${option.label}`;
  }

  // Fallback for timezones not in our list
  return `${getGMTOffset(timezone)} ${timezone.split("/").pop()?.replace(/_/g, " ") || timezone}`;
};
